import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, fromEvent, map, Subject, takeUntil } from 'rxjs';
import { DropdownOption } from './form-combobox.model';

@Component({
  selector: 'flow-form-combobox',
  templateUrl: './form-combobox.component.html',
  styleUrls: ['./form-combobox.component.scss'],
})
export class FormComboboxComponent implements ControlValueAccessor, AfterViewInit, OnChanges {
  @Input() label: string = '';
  @Input() options: DropdownOption[];
  @Input() multiple: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() required: boolean = false;
  @Input() selectAll: boolean = false;
  @Input() infoTooltip: string = '';
  @Input() placeholder: string = '';
  @Input() hasSearch: boolean = false;
  @Input() includeInactive: boolean = false;
  @Input() includeInactiveLabel: string = '';
  @Input() params: any;
  @Input() onSearch: (term: string) => void;
  @Input() onLoadMore: (params: any) => void;
  @Output() search = new EventEmitter<any>();
  @ViewChild('inputElement') inputElement!: ElementRef;

  private destroy$ = new Subject<void>();
  // Set while waiting for the one-shot "fetch everything" response triggered by toggleSelectAll(),
  // so every matching item ends up selected instead of just the page that was loaded already.
  private loadingAllPages = false;
  value: any;

  constructor(@Optional() @Self() protected control: NgControl, private cdr: ChangeDetectorRef) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.options || !this.loadingAllPages) return;

    this.loadingAllPages = false;
    this.selectAllLoadedOptions();
  }

  ngAfterViewInit() {
    if (!this.inputElement) return;
    fromEvent(this.inputElement.nativeElement, 'keyup')
      .pipe(
        map(() => this.inputElement.nativeElement.value),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(val => {
        this.onSearch(val);
        this.cdr.detectChanges();
      });
  }

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onValueChanged(event: any) {
    this.value = event;
    this.onChange(event);
    this.onTouched();
  }

  onSelectChange(value: any) {
    const prevHadInactive = Array.isArray(this.value) && this.value.includes(-1);
    const nextHadInactive = Array.isArray(value) && value.includes(-1);

    this.value = value;
    this.onChange(value);
    this.onTouched();

    if (this.includeInactive && this.hasSearch && prevHadInactive !== nextHadInactive) {
      this.onSearch('');
      this.cdr.detectChanges();
    }
  }

  // isAllSelected is derived from value vs. options rather than tracked as a flag, so it
  // stays correct as more pages load or the user deselects an item by hand. It only reports
  // true once every page has been fetched, since "select all" means every matching item, not
  // just whatever page happened to be loaded already.
  get isAllSelected(): boolean {
    if (!this.options?.length || !Array.isArray(this.value) || this.hasMorePages()) return false;
    const optionIds = this.options.map(o => o.id || o.key);
    const requiredIds = this.includeInactive ? [-1, ...optionIds] : optionIds;
    return requiredIds.every(id => this.value.includes(id));
  }

  toggleSelectAll(): void {
    if (this.isAllSelected) {
      this.loadingAllPages = false;
      this.clearValue();
      return;
    }

    if (this.hasMorePages()) {
      // One request for the full list beats paging through it call-by-call: returnAllResults
      // tells the backend to bypass its page-size cap and return every matching row, rather
      // than us looping loadMore() or requesting a pageSize the server would just clamp down.
      // A fresh object is passed rather than mutating this.params in place, so the host's own
      // pagination state isn't affected by this one-off request.
      this.loadingAllPages = true;
      this.onLoadMore({ ...this.params, pageNumber: 0, returnAllResults: true });
      this.cdr.detectChanges();
    } else {
      this.selectAllLoadedOptions();
    }
  }

  private hasMorePages(): boolean {
    return !!(this.hasSearch && this.params && this.options.length < this.params.totalCount);
  }

  private selectAllLoadedOptions(): void {
    const optionIds = this.options.map(o => o.id || o.key);
    this.value = this.includeInactive ? [-1, ...optionIds] : optionIds;
    this.onSelectChange(this.value);
  }

  clearSearchText(): void {
    this.inputElement.nativeElement.value = '';
    this.onSearch('');
    this.cdr.detectChanges();
  }

  loadMore(): void {
    this.params.pageNumber = this.params.pageNumber + 1;
    this.onLoadMore(this.params);
    this.cdr.detectChanges();
  }

  clearSelection(): void {
    this.loadingAllPages = false;
    this.clearValue();
  }

  // Clearing the selection can drop the "-1" include-inactive sentinel along with everything
  // else, but that's a side effect of wiping the selection, not the user deliberately toggling
  // the include-inactive checkbox — so it must not trigger onSelectChange's search-reset, which
  // would otherwise throw away every already-loaded page and refetch just the first one.
  private clearValue(): void {
    this.value = [];
    this.onChange(this.value);
    this.onTouched();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
